'use client';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import classnames from 'classnames';

import type { EditorProps } from './Editor.types';
import { editorConfig } from './config/editorConfig';
import { ToolbarContext } from './context/ToolbarContext';
import { EditableStatePlugin } from './plugins/EditableStatePlugin';
import { EmojiPickerPlugin } from './plugins/EmojiPickerPlugin';
import { HtmlImportExportPlugin } from './plugins/HtmlImportExportPlugin';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';

import styles from './Editor.module.scss';
import themeStyles from './EditorTheme.module.scss';

const DEFAULT_PLACEHOLDER = 'Enter some text...';

function Editor({
  value,
  onChange,
  className,
  style,
  testId,
  labels,
  placeholder = DEFAULT_PLACEHOLDER,
  error = false,
  disabled = false,
}: EditorProps) {
  const config = {
    ...editorConfig,
    editable: !disabled,
  };

  return (
    <ToolbarContext labels={labels} disabled={disabled}>
      <LexicalComposer initialConfig={config}>
        <div
          className={classnames(
            styles['editor-container'],
            error && styles['editor-container--error'],
            disabled && styles['editor-container--disabled'],
            className,
          )}
          style={style}
          data-testid={testId}
        >
          <ToolbarPlugin labels={labels} />
          <div className={styles['editor-inner']}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={styles['editor-input']}
                  aria-placeholder={placeholder}
                  placeholder={
                    <div className={themeStyles['editor-placeholder']}>
                      {placeholder}
                    </div>
                  }
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <ListPlugin />
            <EditableStatePlugin disabled={disabled} />
            <EmojiPickerPlugin />
            <HtmlImportExportPlugin value={value} onChange={onChange} />
          </div>
        </div>
      </LexicalComposer>
    </ToolbarContext>
  );
}

export { Editor };
